import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { AdminPageHeader } from "@/components/admin/crud-helpers";
import { StatsStrip } from "@/components/admin/stats-strip";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  ArrowLeft,
  FileIcon,
  Star,
  UserPlus,
  MessagesSquare,
  Timer,
  CheckCircle2,
  UserCheck,
  Search,
  Send,
  RefreshCw,
  X,
  Zap,
  User as UserIcon,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export const Route = createFileRoute("/_authenticated/admin/chat")({ component: ChatAdmin });

type Convo = {
  id: string;
  user_id: string;
  subject: string | null;
  status: "active" | "waiting_human" | "closed";
  assignee_id: string | null;
  rating: number | null;
  feedback: string | null;
  created_at: string;
  updated_at: string;
};
type Attachment = { path: string; name: string; size: number; type: string };
type Msg = {
  id: string;
  conversation_id: string;
  role: "user" | "assistant" | "agent" | "system";
  body: string;
  attachments: Attachment[];
  created_at: string;
};
type Profile = {
  id: string;
  display_name: string | null;
  avatar_url: string | null;
  created_at: string | null;
  phone: string | null;
  bio: string | null;
  website: string | null;
};

/** Track online users via Supabase Realtime presence on a shared channel. */
function useOnlineUsers() {
  const [online, setOnline] = useState<Set<string>>(new Set());
  const { user } = useAuth();
  useEffect(() => {
    const ch = supabase.channel("presence:online-users", {
      config: { presence: { key: user?.id ?? crypto.randomUUID() } },
    });
    const sync = () => {
      const state = ch.presenceState() as Record<string, Array<{ user_id?: string }>>;
      const ids = new Set<string>();
      for (const key of Object.keys(state)) {
        // key is the presence key (user id); also inspect payloads for user_id
        if (key) ids.add(key);
        for (const p of state[key] ?? []) if (p.user_id) ids.add(p.user_id);
      }
      setOnline(ids);
    };
    ch.on("presence", { event: "sync" }, sync)
      .on("presence", { event: "join" }, sync)
      .on("presence", { event: "leave" }, sync)
      .subscribe(async (status) => {
        if (status === "SUBSCRIBED" && user?.id) {
          await ch.track({ user_id: user.id, role: "admin", at: new Date().toISOString() });
        }
      });
    return () => {
      supabase.removeChannel(ch);
    };
  }, [user?.id]);
  return online;
}


const statusVariant: Record<Convo["status"], "default" | "secondary" | "outline"> = {
  active: "secondary",
  waiting_human: "default",
  closed: "outline",
};
const statusLabel: Record<Convo["status"], string> = {
  active: "Active",
  waiting_human: "Waiting",
  closed: "Closed",
};
const statusDot: Record<Convo["status"], string> = {
  active: "bg-blue-500",
  waiting_human: "bg-amber-500 animate-pulse",
  closed: "bg-muted-foreground/40",
};

const CANNED = [
  { label: "Greet", text: "Hi 👋 Thanks for reaching out — happy to help!" },
  { label: "Info request", text: "Could you share a bit more detail so I can look into this for you?" },
  { label: "Looking", text: "Give me a moment while I check on this…" },
  { label: "Resolved", text: "Glad we could help! I'll close this out — reply anytime if anything else comes up." },
];

function initials(name?: string | null, fallback = "?") {
  if (!name) return fallback;
  return name.split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 truncate text-xs font-medium">{value}</div>
    </div>
  );
}


function ChatAdmin() {
  const { user } = useAuth();
  const [selected, setSelected] = useState<string | null>(null);
  const [tab, setTab] = useState<"all" | "waiting" | "mine" | "unassigned" | "closed">("all");
  const [search, setSearch] = useState("");
  const qc = useQueryClient();

  const { data: allConvos, isFetching, refetch } = useQuery({
    queryKey: ["chat_conversations", "all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_conversations")
        .select("*")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data as Convo[];
    },
  });

  // Fetch last message preview per convo
  const convoIds = (allConvos ?? []).map((c) => c.id);
  const { data: lastMsgs } = useQuery({
    queryKey: ["chat_last_messages", convoIds.join(",")],
    enabled: convoIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("conversation_id, body, role, created_at")
        .in("conversation_id", convoIds)
        .order("created_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      const map = new Map<string, { body: string; role: string; created_at: string }>();
      for (const m of data as Array<{ conversation_id: string; body: string; role: string; created_at: string }>) {
        if (!map.has(m.conversation_id)) map.set(m.conversation_id, m);
      }
      return map;
    },
  });

  // Fetch profiles for customer display
  const userIds = Array.from(new Set((allConvos ?? []).map((c) => c.user_id).filter(Boolean)));
  const { data: profiles } = useQuery({
    queryKey: ["chat_profiles", userIds.join(",")],
    enabled: userIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, display_name, avatar_url, created_at, phone, bio, website")
        .in("id", userIds);
      if (error) throw error;
      const map = new Map<string, Profile>();
      for (const p of (data ?? []) as Profile[]) map.set(p.id, p);
      return map;
    },
  });

  const onlineUsers = useOnlineUsers();


  useEffect(() => {
    const ch = supabase
      .channel("admin-chat-list")
      .on("postgres_changes", { event: "*", schema: "public", table: "chat_conversations" }, () => {
        qc.invalidateQueries({ queryKey: ["chat_conversations"] });
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages" }, () => {
        qc.invalidateQueries({ queryKey: ["chat_last_messages"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  const filtered = useMemo(() => {
    const list = allConvos ?? [];
    return list.filter((c) => {
      if (tab === "waiting" && c.status !== "waiting_human") return false;
      if (tab === "closed" && c.status !== "closed") return false;
      if (tab === "mine" && c.assignee_id !== user?.id) return false;
      if (tab === "unassigned" && (c.assignee_id || c.status === "closed")) return false;
      if (tab === "all" && c.status === "closed") return false;
      if (search) {
        const s = search.toLowerCase();
        const prof = profiles?.get(c.user_id);
        const hit =
          (c.subject ?? "").toLowerCase().includes(s) ||
          (prof?.display_name ?? "").toLowerCase().includes(s) ||
          (lastMsgs?.get(c.id)?.body ?? "").toLowerCase().includes(s);
        if (!hit) return false;
      }
      return true;
    });
  }, [allConvos, tab, search, user?.id, profiles, lastMsgs]);

  const stats = useMemo(() => {
    const list = allConvos ?? [];
    const waiting = list.filter((c) => c.status === "waiting_human").length;
    const active = list.filter((c) => c.status === "active").length;
    const closed = list.filter((c) => c.status === "closed").length;
    const mine = list.filter((c) => c.assignee_id === user?.id && c.status !== "closed").length;
    const rated = list.filter((c) => c.rating != null);
    const avg = rated.length ? (rated.reduce((a, c) => a + (c.rating ?? 0), 0) / rated.length).toFixed(1) : "—";
    return { total: list.length, waiting, active, closed, mine, avg };
  }, [allConvos, user?.id]);

  const tabs = [
    { key: "all" as const, label: "Open", count: stats.total - stats.closed },
    { key: "waiting" as const, label: "Waiting", count: stats.waiting, urgent: stats.waiting > 0 },
    { key: "mine" as const, label: "Mine", count: stats.mine },
    { key: "unassigned" as const, label: "Unassigned", count: (allConvos ?? []).filter((c) => !c.assignee_id && c.status !== "closed").length },
    { key: "closed" as const, label: "Closed", count: stats.closed },
  ];

  return (
    <div className="space-y-4">
      <AdminPageHeader
        title="Live chat inbox"
        description="Customer chats with AI + human handoff."
        actions={
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        }
      />

      <StatsStrip
        tiles={[
          { label: "Total", value: stats.total, icon: MessagesSquare },
          { label: "Waiting", value: stats.waiting, icon: Timer, tone: stats.waiting ? "warning" : "default" },
          { label: "Active", value: stats.active, icon: UserCheck, tone: "info" },
          { label: "Assigned to me", value: stats.mine, icon: UserIcon, tone: stats.mine ? "success" : "default" },
          { label: "Avg rating", value: stats.avg, icon: Star },
        ]}
      />

      {/* Filter chips + search */}
      <div className="space-y-2">
        <div className="flex flex-wrap gap-1.5">
          {tabs.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                  active
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card hover:bg-muted"
                }`}
              >
                {t.label}
                <span
                  className={`inline-flex min-w-[18px] justify-center rounded-full px-1.5 text-[10px] tabular-nums ${
                    active
                      ? "bg-primary-foreground/20"
                      : t.urgent
                        ? "bg-amber-500/20 text-amber-700 dark:text-amber-400"
                        : "bg-muted text-muted-foreground"
                  }`}
                >
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search subject, customer, or message…"
            className="pl-9 pr-9"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid gap-3 lg:grid-cols-[380px_1fr]">
        {/* List — hidden on mobile when a conversation is selected */}
        <div
          className={`space-y-2 lg:max-h-[calc(100vh-360px)] lg:overflow-y-auto lg:pr-1 ${
            selected ? "hidden lg:block" : ""
          }`}
        >
          {!filtered.length && (
            <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
              <MessagesSquare className="mx-auto mb-2 h-8 w-8 opacity-40" />
              No chats here yet.
            </div>
          )}
          {filtered.map((c) => {
            const active = selected === c.id;
            const prof = profiles?.get(c.user_id);
            const last = lastMsgs?.get(c.id);
            const name = prof?.display_name ?? `Customer ${c.user_id.slice(0, 6)}`;
            return (
              <button
                key={c.id}
                onClick={() => setSelected(c.id)}
                className={`w-full rounded-lg border p-3 text-left transition-colors ${
                  active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-muted/50"
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="relative shrink-0">
                    <Avatar className="h-9 w-9">
                      <AvatarImage src={prof?.avatar_url ?? undefined} />
                      <AvatarFallback className="text-xs">{initials(name, "C")}</AvatarFallback>
                    </Avatar>
                    <span
                      className={`absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-card ${
                        onlineUsers.has(c.user_id) ? "bg-emerald-500" : statusDot[c.status]
                      }`}
                      title={onlineUsers.has(c.user_id) ? "Online now" : statusLabel[c.status]}
                      aria-hidden
                    />

                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-2">
                      <div className="truncate text-sm font-medium">{name}</div>
                      <div className="shrink-0 text-[10px] text-muted-foreground">
                        {formatDistanceToNow(new Date(c.updated_at), { addSuffix: true })}
                      </div>
                    </div>
                    <div className="truncate text-xs text-muted-foreground">{c.subject ?? "(no subject)"}</div>
                    {last && (
                      <div className="mt-0.5 truncate text-[11px] text-muted-foreground/80">
                        <span className="font-medium">
                          {last.role === "user" ? "" : last.role === "agent" ? "You: " : "AI: "}
                        </span>
                        {last.body}
                      </div>
                    )}
                    <div className="mt-1.5 flex items-center gap-1.5">
                      <Badge variant={statusVariant[c.status]} className="text-[10px]">
                        {statusLabel[c.status]}
                      </Badge>
                      {c.assignee_id === user?.id && (
                        <Badge variant="outline" className="border-emerald-500/40 text-[10px] text-emerald-600 dark:text-emerald-400">
                          Mine
                        </Badge>
                      )}
                      {!c.assignee_id && c.status !== "closed" && (
                        <Badge variant="outline" className="text-[10px]">Unassigned</Badge>
                      )}
                      {c.rating != null && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] text-muted-foreground">
                          <Star className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />
                          {c.rating}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        <div className={`min-w-0 ${selected ? "" : "hidden lg:block"}`}>
          {selected ? (
            <ConvoDetail
              id={selected}
              onBack={() => setSelected(null)}
              profile={profiles?.get((allConvos ?? []).find((c) => c.id === selected)?.user_id ?? "") ?? null}
              isOnline={onlineUsers.has((allConvos ?? []).find((c) => c.id === selected)?.user_id ?? "")}
            />

          ) : (
            <div className="hidden h-full min-h-[400px] items-center justify-center rounded-lg border border-dashed border-border bg-card p-12 text-center text-sm text-muted-foreground lg:flex">
              <div>
                <MessagesSquare className="mx-auto mb-3 h-10 w-10 opacity-40" />
                Select a conversation to reply.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ConvoDetail({
  id,
  onBack,
  profile,
  isOnline,
}: {
  id: string;
  onBack: () => void;
  profile: Profile | null;
  isOnline: boolean;
}) {
  const [infoOpen, setInfoOpen] = useState(false);

  const { user } = useAuth();
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: convo } = useQuery({
    queryKey: ["chat_conversations", id],
    queryFn: async () => {
      const { data, error } = await supabase.from("chat_conversations").select("*").eq("id", id).single();
      if (error) throw error;
      return data as Convo;
    },
  });

  const { data: messages } = useQuery({
    queryKey: ["chat_messages", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("chat_messages")
        .select("*")
        .eq("conversation_id", id)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as unknown as Msg[];
    },
  });

  useEffect(() => {
    const ch = supabase
      .channel(`admin-chat:${id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_messages", filter: `conversation_id=eq.${id}` },
        () => qc.invalidateQueries({ queryKey: ["chat_messages", id] }),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "chat_conversations", filter: `id=eq.${id}` },
        () => qc.invalidateQueries({ queryKey: ["chat_conversations", id] }),
      )
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [id, qc]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const join = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("chat_conversations")
        .update({ assignee_id: user?.id, status: "waiting_human" })
        .eq("id", id);
      if (error) throw error;
      await supabase.from("chat_messages").insert({
        conversation_id: id,
        role: "system",
        body: "A support agent has joined the chat.",
      });
    },
    onSuccess: () => {
      toast.success("Joined chat");
      qc.invalidateQueries({ queryKey: ["chat_conversations"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const unassign = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("chat_conversations").update({ assignee_id: null }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Unassigned");
      qc.invalidateQueries({ queryKey: ["chat_conversations"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const close = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("chat_conversations").update({ status: "closed" }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Chat closed");
      qc.invalidateQueries({ queryKey: ["chat_conversations"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const reopen = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from("chat_conversations").update({ status: "active" }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Chat reopened");
      qc.invalidateQueries({ queryKey: ["chat_conversations"] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  const send = useMutation({
    mutationFn: async () => {
      const text = reply.trim();
      if (!text) throw new Error("Reply cannot be empty");
      const { error } = await supabase.from("chat_messages").insert({
        conversation_id: id,
        role: "agent",
        author_id: user?.id,
        body: text,
      });
      if (error) throw error;
      await supabase.from("chat_conversations").update({ updated_at: new Date().toISOString() }).eq("id", id);
    },
    onSuccess: () => setReply(""),
    onError: (e: any) => toast.error(e.message),
  });

  const openAttachment = async (att: Attachment) => {
    const { data, error } = await supabase.storage.from("ticket-attachments").createSignedUrl(att.path, 60);
    if (error || !data?.signedUrl) return toast.error("Could not open attachment");
    window.open(data.signedUrl, "_blank");
  };

  if (!convo)
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        Loading conversation…
      </div>
    );

  const isClosed = convo.status === "closed";
  const isMine = convo.assignee_id === user?.id;
  const name = profile?.display_name ?? `Customer ${convo.user_id.slice(0, 6)}`;

  return (
    <div className="flex flex-col rounded-lg border border-border bg-card">
      {/* Header */}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 border-b border-border p-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Button variant="ghost" size="icon" onClick={onBack} className="lg:hidden">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <Avatar className="h-9 w-9 shrink-0">
            <AvatarImage src={profile?.avatar_url ?? undefined} />
            <AvatarFallback className="text-xs">{initials(name, "C")}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm font-semibold">{name}</span>
              <span
                className={`h-2 w-2 shrink-0 rounded-full ${
                  isOnline ? "bg-emerald-500 animate-pulse" : statusDot[convo.status]
                }`}
                aria-hidden
              />
            </div>
            <div className="truncate text-xs text-muted-foreground">
              <span className={isOnline ? "text-emerald-600 dark:text-emerald-400" : ""}>
                {isOnline ? "● Online now" : statusLabel[convo.status]}
              </span>
              {" · "}
              {convo.subject ?? "(no subject)"}
            </div>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setInfoOpen((v) => !v)}
            title="Customer details"
          >
            <UserIcon className="h-3.5 w-3.5" />
          </Button>
          {!isClosed && !isMine && (
            <Button size="sm" variant="outline" onClick={() => join.mutate()} disabled={join.isPending}>
              <UserPlus className="mr-1 h-3.5 w-3.5" /> Take
            </Button>
          )}
          {!isClosed && isMine && (
            <Button size="sm" variant="ghost" onClick={() => unassign.mutate()} disabled={unassign.isPending}>
              Unassign
            </Button>
          )}
          {!isClosed ? (
            <Button size="sm" variant="outline" onClick={() => close.mutate()} disabled={close.isPending}>
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Close
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
              <RefreshCw className="mr-1 h-3.5 w-3.5" /> Reopen
            </Button>
          )}
        </div>
      </div>

      {/* Customer details panel */}
      {infoOpen && (
        <div className="border-b border-border bg-muted/30 p-4 text-sm">
          <div className="mb-2 flex items-center justify-between">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Customer details
            </div>
            <button onClick={() => setInfoOpen(false)} className="rounded p-1 text-muted-foreground hover:bg-muted">
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <InfoRow label="Name" value={profile?.display_name ?? "—"} />
            <InfoRow
              label="Status"
              value={
                isOnline ? (
                  <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                    Online
                  </span>
                ) : (
                  <span className="text-muted-foreground">Offline</span>
                )
              }
            />
            <InfoRow label="Phone" value={profile?.phone ?? "—"} />
            <InfoRow label="Website" value={profile?.website ?? "—"} />
            <InfoRow
              label="Customer since"
              value={profile?.created_at ? formatDistanceToNow(new Date(profile.created_at), { addSuffix: true }) : "—"}
            />
            <InfoRow
              label="Chat opened"
              value={formatDistanceToNow(new Date(convo.created_at), { addSuffix: true })}
            />
            <InfoRow label="Messages" value={String(messages?.length ?? 0)} />
            <InfoRow
              label="User ID"
              value={<code className="rounded bg-background px-1 py-0.5 text-[10px]">{convo.user_id.slice(0, 12)}…</code>}
            />
            {profile?.bio && (
              <div className="sm:col-span-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Bio</div>
                <div className="mt-0.5 text-xs">{profile.bio}</div>
              </div>
            )}
          </div>
        </div>
      )}

      {convo.rating != null && (
        <div className="flex items-center gap-2 border-b border-border bg-muted/30 p-3 text-sm">
          <span className="flex">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                className={`h-4 w-4 ${
                  n <= (convo.rating ?? 0) ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"
                }`}
              />
            ))}
          </span>

          {convo.feedback && <span className="min-w-0 truncate text-muted-foreground">"{convo.feedback}"</span>}
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="h-[420px] space-y-3 overflow-y-auto bg-muted/20 p-4 sm:h-[480px]">
        {!messages?.length && (
          <div className="grid h-full place-items-center text-sm text-muted-foreground">No messages yet.</div>
        )}
        {messages?.map((m, idx) => {
          const prev = idx > 0 ? messages[idx - 1] : null;
          const showAvatar = !prev || prev.role !== m.role;
          if (m.role === "system") {
            return (
              <div key={m.id} className="text-center text-[11px] italic text-muted-foreground">
                {m.body}
              </div>
            );
          }
          const isCustomer = m.role === "user";
          return (
            <div key={m.id} className={`flex items-end gap-2 ${isCustomer ? "" : "flex-row-reverse"}`}>
              <div className="w-7 shrink-0">
                {showAvatar && (
                  <Avatar className="h-7 w-7">
                    {isCustomer ? (
                      <>
                        <AvatarImage src={profile?.avatar_url ?? undefined} />
                        <AvatarFallback className="text-[10px]">{initials(name, "C")}</AvatarFallback>
                      </>
                    ) : (
                      <AvatarFallback
                        className={`text-[10px] ${
                          m.role === "agent"
                            ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                            : "bg-primary/20 text-primary"
                        }`}
                      >
                        {m.role === "agent" ? "A" : "AI"}
                      </AvatarFallback>
                    )}
                  </Avatar>
                )}
              </div>
              <div className="min-w-0 max-w-[80%]">
                <div
                  className={`rounded-2xl px-3 py-2 text-sm ${
                    isCustomer
                      ? "rounded-bl-sm bg-background border border-border"
                      : m.role === "agent"
                        ? "rounded-br-sm bg-emerald-500/10 border border-emerald-500/30"
                        : "rounded-br-sm bg-primary/10 border border-primary/20"
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words">{m.body}</p>
                  {Array.isArray(m.attachments) && m.attachments.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {m.attachments.map((a, i) => (
                        <button
                          key={i}
                          onClick={() => openAttachment(a)}
                          className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-0.5 text-xs hover:bg-muted"
                        >
                          <FileIcon className="h-3 w-3" /> {a.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <div className={`mt-1 text-[10px] text-muted-foreground ${isCustomer ? "text-left" : "text-right"}`}>
                  {m.role === "user" ? "Customer" : m.role === "agent" ? "You" : "AI"} ·{" "}
                  {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Composer */}
      {!isClosed && (
        <div className="space-y-2 border-t border-border p-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <Zap className="h-3 w-3 text-muted-foreground" />
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">Quick reply</span>
            {CANNED.map((c) => (
              <button
                key={c.label}
                onClick={() => setReply(c.text)}
                className="rounded-full border border-border bg-background px-2.5 py-0.5 text-[11px] hover:bg-muted"
              >
                {c.label}
              </button>
            ))}
          </div>
          <Textarea
            rows={3}
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            placeholder={isMine ? "Type your reply…" : "Take the chat to reply, or send anyway…"}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                send.mutate();
              }
            }}
          />
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-muted-foreground">
              ⌘/Ctrl+Enter to send · {reply.length} chars
            </span>
            <Button onClick={() => send.mutate()} disabled={send.isPending || !reply.trim()}>
              <Send className="mr-1 h-3.5 w-3.5" />
              {send.isPending ? "Sending…" : "Send reply"}
            </Button>
          </div>
        </div>
      )}
      {isClosed && (
        <div className="border-t border-border bg-muted/20 p-4 text-center text-xs text-muted-foreground">
          This chat is closed. <Label className="sr-only">Reopen from the header to reply.</Label>
          Reopen from the header to reply.
        </div>
      )}
    </div>
  );
}
